import type { Dispatch, SetStateAction } from "react";
import {
  generateTournamentBracket,
  generateTournamentSchedule,
  saveTournamentScheduleConfig,
  type AdminTournamentBracket,
  type AdminTournamentSchedule,
} from "../services/eventRegistrationAdminService";
import {
  createScheduleDraft,
  schedulePayloadFromDraft,
} from "../utils/tournamentOperations";
import type { AdminTournamentManualPair } from "./useAdminTournamentResources";

type ManualGroupOption = {
  id: string;
  name: string;
};

type UseAdminTournamentGenerationInput = {
  categoryKey: string;
  hasPendingOperationChanges: boolean;
  loadSchedule: () => Promise<void>;
  manualAssignments: Record<string, string>;
  manualGroupOptions: ManualGroupOption[];
  manualPairs: AdminTournamentManualPair[];
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  schedule: AdminTournamentSchedule | null;
  scheduleChanged: boolean;
  scheduleConfigurationMissing: boolean;
  scheduleDraft: ReturnType<typeof createScheduleDraft>;
  setBracket: Dispatch<SetStateAction<AdminTournamentBracket | null>>;
  setSaving: Dispatch<SetStateAction<boolean>>;
  setSchedule: Dispatch<SetStateAction<AdminTournamentSchedule | null>>;
  setScheduleDraft: Dispatch<SetStateAction<ReturnType<typeof createScheduleDraft>>>;
  setScheduleEdits: Dispatch<SetStateAction<Record<string, { courtName: string }>>>;
  token: string;
  tournamentConfigurationBlocker: string;
  tournamentId: number;
};

export function useAdminTournamentGeneration({
  categoryKey,
  hasPendingOperationChanges,
  loadSchedule,
  manualAssignments,
  manualGroupOptions,
  manualPairs,
  onError,
  onSuccess,
  schedule,
  scheduleChanged,
  scheduleConfigurationMissing,
  scheduleDraft,
  setBracket,
  setSaving,
  setSchedule,
  setScheduleDraft,
  setScheduleEdits,
  token,
  tournamentConfigurationBlocker,
  tournamentId,
}: UseAdminTournamentGenerationInput) {
  const savePendingOperationChanges = async () => {
    if (!categoryKey || !hasPendingOperationChanges) return true;
    const shouldSaveSchedule = scheduleChanged || scheduleConfigurationMissing;
    const schedulePayload = shouldSaveSchedule
      ? schedulePayloadFromDraft(scheduleDraft)
      : null;
    if (shouldSaveSchedule && !schedulePayload) {
      onError?.("Informe período e pelo menos uma quadra.");
      return false;
    }
    setSaving(true);
    const savedLabels: string[] = [];
    try {
      if (shouldSaveSchedule && schedulePayload) {
        const result = await saveTournamentScheduleConfig(
          token,
          tournamentId,
          schedulePayload,
        );
        setSchedule((current) => ({
          tournamentId,
          slots: current?.slots || [],
          config: result.config,
        }));
        setScheduleDraft(createScheduleDraft(result.config));
        savedLabels.push("programação");
      }
      await loadSchedule();
      onSuccess?.(
        `${savedLabels.join(" e ")} salvas. Geração de chave e timeline liberada.`,
      );
      return true;
    } catch (error) {
      onError?.(
        error instanceof Error
          ? error.message
          : "Não foi possível salvar as alterações.",
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const persistScheduleConfigForGeneration = async () => {
    const schedulePayload = schedulePayloadFromDraft(scheduleDraft);
    if (!schedulePayload) {
      throw new Error("Informe período e pelo menos uma quadra.");
    }
    if (scheduleChanged || !schedule?.config) {
      const result = await saveTournamentScheduleConfig(
        token,
        tournamentId,
        schedulePayload,
      );
      setSchedule((current) => ({
        tournamentId,
        slots: current?.slots || [],
        config: result.config,
      }));
      setScheduleDraft(createScheduleDraft(result.config));
      return {
        dayStartAt: result.config.dayStartAt,
        dayEndAt: result.config.dayEndAt,
        courtNames: result.config.courtNames,
        defaultMatchDurationMinutes: result.config.defaultMatchDurationMinutes,
        minRestMinutes: result.config.minRestMinutes || 0,
        operatingHours:
          result.config.operatingHours || schedulePayload.operatingHours,
        roundPolicy: result.config.roundPolicy || "strict",
      };
    }
    return schedulePayload;
  };

  const generateTimeline = async () => {
    if (tournamentConfigurationBlocker) {
      onError?.(tournamentConfigurationBlocker);
      return;
    }
    setSaving(true);
    try {
      const config = await persistScheduleConfigForGeneration();
      const nextSchedule = await generateTournamentSchedule(token, tournamentId, {
        config,
        regenerateAuto: true,
      });
      setSchedule(nextSchedule);
      setScheduleEdits({});
      await loadSchedule();
      onSuccess?.(
        nextSchedule.restReducedToZero
          ? "Timeline geral gerada para todas as categorias com descanso mínimo reduzido para 0."
          : "Timeline geral gerada para todas as categorias.",
      );
    } catch (error) {
      onError?.(
        error instanceof Error
          ? `Não foi possível gerar a timeline: ${error.message}`
          : "Não foi possível gerar a timeline.",
      );
    } finally {
      setSaving(false);
    }
  };

  const generateBracket = async (
    seedMode: "ranking" | "random" | "manual" = "ranking",
  ) => {
    if (!categoryKey) return;
    if (tournamentConfigurationBlocker) {
      onError?.(tournamentConfigurationBlocker);
      return;
    }
    setSaving(true);
    try {
      const next = await generateTournamentBracket(token, tournamentId, categoryKey, {
        seedMode,
      });
      setBracket(next);
      onSuccess?.("Chave gerada. Gere a timeline geral após revisar as categorias.");
    } catch (error) {
      onError?.(
        error instanceof Error ? error.message : "Não foi possível gerar a chave.",
      );
    } finally {
      setSaving(false);
    }
  };

  const generateManualBracket = async () => {
    if (!categoryKey) return;
    if (tournamentConfigurationBlocker) {
      onError?.(tournamentConfigurationBlocker);
      return;
    }
    if (manualPairs.length < 3) {
      onError?.("É preciso ter ao menos 3 duplas confirmadas para montar grupos.");
      return;
    }
    const manualGroups = manualGroupOptions.map((group) => ({
      id: group.id,
      name: group.name,
      registrationIds: manualPairs
        .filter((pair) => manualAssignments[String(pair.registrationId)] === group.id)
        .map((pair) => pair.registrationId),
    }));
    const invalidGroup = manualGroups.find(
      (group) => group.registrationIds.length < 2,
    );
    if (invalidGroup) {
      onError?.("Cada grupo precisa ter pelo menos 2 duplas.");
      return;
    }
    const assignedCount = manualGroups.reduce(
      (total, group) => total + group.registrationIds.length,
      0,
    );
    if (assignedCount !== manualPairs.length) {
      onError?.("Coloque todas as duplas confirmadas em um grupo.");
      return;
    }
    setSaving(true);
    try {
      const next = await generateTournamentBracket(token, tournamentId, categoryKey, {
        seedMode: "manual",
        manualSeeds: manualGroups.flatMap((group) => group.registrationIds),
        manualGroups,
      });
      setBracket(next);
      onSuccess?.("Chave manual gerada. Gere a timeline geral após revisar as categorias.");
    } catch (error) {
      onError?.(
        error instanceof Error
          ? error.message
          : "Não foi possível gerar a chave manual.",
      );
    } finally {
      setSaving(false);
    }
  };

  return {
    generateBracket,
    generateTimeline,
    generateManualBracket,
    savePendingOperationChanges,
  };
}
